import { __awaiter, __generator } from "tslib";
import React, { useEffect, useState, useRef } from "react";
import { VeltContext } from "../../context/SnippylyContext";
import loadVelt from "../../loadSnippyly";
import { VELT_SDK_INIT_EVENT, VELT_SDK_VERSION, INTEGRITY_MAP, } from "../../constants";
import { deepCompare } from "../../utils/propUtils";
var SnippylyProvider = function (props) {
    var apiKey = props.apiKey, user = props.user, config = props.config, documentId = props.documentId, language = props.language, translations = props.translations, autoTranslation = props.autoTranslation, userDataProvider = props.userDataProvider, dataProviders = props.dataProviders, encryptionProvider = props.encryptionProvider, authProvider = props.authProvider, permissionProvider = props.permissionProvider, onClientLoad = props.onClientLoad, children = props.children;
    var _a = useState(null), client = _a[0], setClient = _a[1];
    var prevAuthProviderRef = useRef(undefined);
    var prevUserDataProviderRef = useRef(undefined);
    var prevDataProvidersRef = useRef(undefined);
    var prevPermissionProviderRef = useRef(undefined);
    /**
     * Tracks whether the component is currently mounted.
     * Used to prevent state updates and async operations on unmounted components.
     *
     * ## Issue (Rapid Mount/Unmount)
     * When VeltProvider is rapidly mounted/unmounted (e.g., conditional rendering
     * based on state changes, React Strict Mode, or route transitions), multiple
     * component instances can have async operations (like Velt.init()) in flight.
     * When these async operations complete, they try to call setClient() on
     * unmounted component instances, causing React warnings and incorrect state.
     *
     * ## Fix
     * Track mount state and check it before/after async operations to bail out
     * if the component has been unmounted.
     */
    var isMountedRef = useRef(true);
    useEffect(function () {
        // IMPORTANT: Reset isMountedRef to true on every mount/remount.
        // This is critical for React Strict Mode which simulates unmount/remount:
        // 
        // React Strict Mode sequence:
        // 1. Mount → isMountedRef = true (initial value)
        // 2. Effect runs
        // 3. Strict Mode simulates unmount → cleanup sets isMountedRef = false
        // 4. Strict Mode simulates remount → effect runs again
        // 
        // Without this reset, step 4 would still see isMountedRef = false from step 3,
        // causing initVelt to skip even though the component is actually mounted.
        isMountedRef.current = true;
        // Cleanup: mark component as unmounted
        return function () {
            isMountedRef.current = false;
        };
    }, []);
    useEffect(function () {
        var _a;
        // Pre-bootstrap selfHosted stash: the SDK's default Firebase app initializes while
        // velt.js EVALUATES (any velt-* elements already in the DOM upgrade synchronously
        // during customElements.define), which is BEFORE the script's load event and therefore
        // BEFORE Velt.init() can write this stash itself. Writing it here — before the script
        // tag is appended — is the only ordering that guarantees selfHosted.firebaseConfig
        // applies. Velt.init() later writes the same value; sdk.js compares by JSON and stays
        // silent when they match.
        if (typeof window !== "undefined" && (config === null || config === void 0 ? void 0 : config.selfHosted)) {
            window.__VELT_PRE_BOOTSTRAP_SELF_HOSTED__ = config.selfHosted;
        }
        // Store cleanup function returned from loadVelt to remove event listeners on unmount
        var cleanupLoadVelt;
        if (apiKey) {
            var staging = config === null || config === void 0 ? void 0 : config.staging;
            var develop = config === null || config === void 0 ? void 0 : config.develop;
            var version = (config === null || config === void 0 ? void 0 : config.version) || VELT_SDK_VERSION;
            var integrity = !!(config === null || config === void 0 ? void 0 : config.integrity);
            var integrityValue = "";
            if (integrity) {
                if (develop || staging) {
                    if (config === null || config === void 0 ? void 0 : config.sriv) {
                        integrityValue = config === null || config === void 0 ? void 0 : config.sriv;
                    }
                }
                else {
                    if (INTEGRITY_MAP[version]) {
                        integrityValue = INTEGRITY_MAP[version];
                    }
                }
            }
            // Rapid mount/unmount issue found in OpenEnvoy client code with slow network.
            // 
            // Scenario: VeltProvider is conditionally rendered and toggles rapidly while
            // the Velt script is loading over a slow network connection.
            // 
            // We check if Velt is already loaded (window.Velt exists) and call initVelt
            // directly. If not loaded, we call loadVelt which handles the script loading
            // and returns a cleanup function to remove event listeners.
            if (window === null || window === void 0 ? void 0 : window.Velt) {
                initVelt();
            }
            else {
                // loadVelt returns a cleanup function that removes the load event listener.
                // This prevents the callback from firing on destroyed component instances.
                cleanupLoadVelt = loadVelt(function () {
                    initVelt();
                }, version, staging, develop, ((_a = config === null || config === void 0 ? void 0 : config.proxyConfig) === null || _a === void 0 ? void 0 : _a.cdnHost) || (config === null || config === void 0 ? void 0 : config.proxyDomain), integrity, integrityValue);
            }
        }
        // Cleanup: remove event listener when component unmounts.
        // This is critical for rapid mount/unmount scenarios - without this cleanup,
        // the load callback would fire on destroyed component instances and try to
        // call initVelt, which would then try to update state on an unmounted component.
        return function () {
            if (cleanupLoadVelt) {
                cleanupLoadVelt();
            }
        };
    }, []);
    useEffect(function () {
        if (!deepCompare(prevUserDataProviderRef.current, userDataProvider)) {
            if (client && userDataProvider) {
                if (typeof (client === null || client === void 0 ? void 0 : client.setUserDataProvider) === "function") {
                    client === null || client === void 0 ? void 0 : client.setUserDataProvider(userDataProvider);
                }
                prevUserDataProviderRef.current = userDataProvider;
            }
            else {
                // Only update ref to undefined if client is available (to track removal)
                // If client is not available, keep old ref so we can set provider when client becomes available
                if (client) {
                    prevUserDataProviderRef.current = userDataProvider;
                }
            }
        }
    }, [client, userDataProvider]);
    useEffect(function () {
        if (!deepCompare(prevDataProvidersRef.current, dataProviders)) {
            if (client && dataProviders) {
                if (typeof (client === null || client === void 0 ? void 0 : client.setDataProviders) === "function") {
                    client === null || client === void 0 ? void 0 : client.setDataProviders(dataProviders);
                }
                prevDataProvidersRef.current = dataProviders;
            }
            else {
                // Only update ref to undefined if client is available (to track removal)
                // If client is not available, keep old ref so we can set provider when client becomes available
                if (client) {
                    prevDataProvidersRef.current = dataProviders;
                }
            }
        }
    }, [client, dataProviders]);
    useEffect(function () {
        if (client && encryptionProvider) {
            if (typeof (client === null || client === void 0 ? void 0 : client.setEncryptionProvider) === "function") {
                client === null || client === void 0 ? void 0 : client.setEncryptionProvider(encryptionProvider);
            }
        }
    }, [client, encryptionProvider]);
    useEffect(function () {
        if (!deepCompare(prevAuthProviderRef.current, authProvider)) {
            if (client && authProvider && authProvider.user) {
                client.setVeltAuthProvider(authProvider);
                prevAuthProviderRef.current = authProvider;
            }
            else {
                // Only update ref to undefined if client is available (to track removal)
                // If client is not available, keep old ref so we can set provider when client becomes available
                if (client) {
                    prevAuthProviderRef.current = authProvider;
                }
            }
        }
    }, [client, authProvider]);
    useEffect(function () {
        if (!deepCompare(prevPermissionProviderRef.current, permissionProvider)) {
            if (client && permissionProvider) {
                if (typeof (client === null || client === void 0 ? void 0 : client.setPermissionProvider) === "function") {
                    client === null || client === void 0 ? void 0 : client.setPermissionProvider(permissionProvider);
                }
                prevPermissionProviderRef.current = permissionProvider;
            }
            else {
                // Only update ref to undefined if client is available (to track removal)
                // If client is not available, keep old ref so we can set provider when client becomes available
                if (client) {
                    prevPermissionProviderRef.current = permissionProvider;
                }
            }
        }
    }, [client, permissionProvider]);
    /**
     * Initializes the Velt SDK with proper mount state checks.
     *
     * ## Async Safety Pattern
     * This function contains async operations (Velt.init). During the await,
     * the component might unmount. We must check isMountedRef:
     * 1. BEFORE starting - skip if already unmounted
     * 2. AFTER await completes - abort if unmounted during the wait
     *
     * Without these checks, setClient() would be called on unmounted components,
     * causing React warnings and potential memory leaks.
     */
    var initVelt = function () { return __awaiter(void 0, void 0, void 0, function () {
        var velt, event;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    // CHECK 1: Skip initialization if component has already been unmounted.
                    // This handles the case where cleanup ran before initVelt was called.
                    if (!isMountedRef.current) {
                        return [2 /*return*/];
                    }
                    if (!config) return [3 /*break*/, 2];
                    if (config.staging) {
                        delete config.staging;
                    }
                    if (config.develop) {
                        delete config.develop;
                    }
                    if (config === null || config === void 0 ? void 0 : config.version) {
                        config === null || config === void 0 ? true : delete config.version;
                    }
                    if (config === null || config === void 0 ? void 0 : config.proxyDomain) {
                        delete config.proxyDomain;
                    }
                    return [4 /*yield*/, ((_a = window.Velt) === null || _a === void 0 ? void 0 : _a.init(apiKey, config))];
                case 1:
                    velt = _c.sent();
                    return [3 /*break*/, 4];
                case 2: return [4 /*yield*/, ((_b = window.Velt) === null || _b === void 0 ? void 0 : _b.init(apiKey))];
                case 3:
                    velt = _c.sent();
                    _c.label = 4;
                case 4:
                    // CHECK 2: Abort if component unmounted during the await.
                    // Velt.init() is async and can take time. The component might have
                    // been unmounted while we were waiting. Check again before proceeding
                    // with state updates and side effects.
                    if (!isMountedRef.current) {
                        return [2 /*return*/];
                    }
                    // Set language
                    if (language && (velt === null || velt === void 0 ? void 0 : velt.setLanguage)) {
                        velt === null || velt === void 0 ? void 0 : velt.setLanguage(language);
                    }
                    // Set translations
                    if (translations && (velt === null || velt === void 0 ? void 0 : velt.setTranslations)) {
                        if (typeof translations === "object") {
                            Object.keys(translations).forEach(function (languageCode) {
                                velt === null || velt === void 0 ? void 0 : velt.setTranslations(languageCode, translations[languageCode] || {});
                            });
                        }
                    }
                    if (typeof autoTranslation === "boolean") {
                        if (autoTranslation && (velt === null || velt === void 0 ? void 0 : velt.enableAutoTranslation)) {
                            velt === null || velt === void 0 ? void 0 : velt.enableAutoTranslation();
                        }
                        else if (!autoTranslation && (velt === null || velt === void 0 ? void 0 : velt.disableAutoTranslation)) {
                            velt === null || velt === void 0 ? void 0 : velt.disableAutoTranslation();
                        }
                    }
                    if (velt === null || velt === void 0 ? void 0 : velt.st) {
                        velt === null || velt === void 0 ? void 0 : velt.st("react");
                    }
                    setClient(velt);
                    if (onClientLoad) {
                        onClientLoad(velt);
                    }
                    if (!user) return [3 /*break*/, 6];
                    return [4 /*yield*/, (velt === null || velt === void 0 ? void 0 : velt.identify(user))];
                case 5:
                    _c.sent();
                    _c.label = 6;
                case 6:
                    if (documentId) {
                        velt === null || velt === void 0 ? void 0 : velt.setDocumentId(documentId);
                    }
                    event = new CustomEvent(VELT_SDK_INIT_EVENT, { detail: velt });
                    window.dispatchEvent(event);
                    return [2 /*return*/];
            }
        });
    }); };
    return (React.createElement(React.Fragment, null,
        React.createElement(VeltContext.Provider, { value: { client: client } }, children)));
};
export default SnippylyProvider;
